import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { getOrderByNumber, getProductBySlug } from '../../api/apis';
import { IProduct } from '../../lib/interfaces/Product';
import ProductOrderForm from './ProductOrderForm';
import ProductOrderSummary from './ProductOrderSummary';
import { FormField } from '../../lib/interfaces/Form';
import PageLoader from '../Simple/Loader/PageLoader';
import { createOrder } from '../../api/apiRequests/orders';
import './ProductOrder.scss';
import { IOrder } from '../../lib/interfaces/Order';
import {
	CONTENT_FIELDS,
	getContentFieldValue,
} from '../../lib/helpers/content.helper';
import PropductOrderStepper from './ProductOrderStepper';
import CalendarSlotSelection from './CalendarSlotSelection';
import ServicePaymentPage from '../Payment/PaymentPage';
// import CalendarSlotSelection from './CalendarSlotSelection';

const ProductOrder: FC = () => {
	const { slug = '' } = useParams();
	const [searchParams] = useSearchParams();
	const { t, i18n } = useTranslation();
	const [isFormStep, setIsFormStep] = useState<boolean>(true);
	const [currentFormFields, setCurrentFormFields] = useState<FormField[]>([]);
	const [orderFormValues, setOrderFormValues] = useState<any>({});
	const [backOrder, setBackOrder] = useState<string | null>(null);
	const [activeStep, setActiveStep] = useState<number>(0);
	const [currentOrderNumber, setCurrentOrderNumber] = useState<
		number | undefined
	>();
	const currentLanguage: string = i18n.language;
	const hasBackOrder = searchParams.get('back_order');

	const calendlyPaymentFlow = true;

	useEffect(() => {
		if (hasBackOrder) {
			setBackOrder(hasBackOrder);
		}
	}, [hasBackOrder]);

	const { data: productData } = useQuery<IProduct, Error>(
		['product', slug],
		() => getProductBySlug(slug),
	);

	const { data: backOrderData } = useQuery<IOrder, Error>(
		['backOrder', backOrder],
		() => getOrderByNumber(Number(backOrder)),
		{
			enabled: !!backOrder,
		},
	);

	console.log(':backOrderData: ', backOrderData);

	const createOrderMutation = useMutation<IOrder, AxiosError<any>, FormData>(
		(order: FormData) => createOrder(order),
	);

	const { isLoading } = createOrderMutation;

	const productName = getContentFieldValue({
		entityContent: productData?.content,
		field: CONTENT_FIELDS.NAME,
		language: currentLanguage,
	});

	const createCalendlyOrder = (values: any) => {
		const body = new FormData();
		const order = {
			product: productData?.id,
			quantity: 1,
			orderDetails: {
				...values,
			},
		};

		if (orderFormValues.cv && orderFormValues.cv instanceof File) {
			delete order.orderDetails.cv;
		}
		order.orderDetails = JSON.stringify(order.orderDetails);
		body.append('cv', orderFormValues.cv);

		Object.entries(order).forEach(([key, value]) => body.append(key, value));

		createOrderMutation.mutate(body, {
			onSuccess(data) {
				console.log('create order success: ', data);
				setCurrentOrderNumber(data.orderNumber);
				// window.location.href = data.url;
				// navigate(`${paths.payment.servicePayment}/${data.orderNumber}`);
				setActiveStep(1);
			},
		});
	};

	const saveFormValues = (values: any, fields: FormField[]) => {
		setTimeout(() => {
			console.log('values ', values);
			// setIsFormStep(false);
			setOrderFormValues(values);
			setCurrentFormFields(fields);
			createCalendlyOrder(values);
		}, 1);
	};

	const onPurchase = () => {
		// TODO: REMOVE
		if (calendlyPaymentFlow) {
			window.location.href = 'https://calendly.com/codeway-today/mock-interview';
		}
	};

	const onCalendlyTimeSelection = () => {
		setActiveStep(2);
	};

	const onCalendlyScheduled = () => {
		setActiveStep(3);
	};

	return (
		<div className="ProductOrder">
			<div
				style={{
					backgroundImage: `url(${productData?.imageUrl})`,
					height: '150px',
					width: '100%',
					marginTop: '60px',
					backgroundPosition: 'center',
				}}
			/>
			<div className="ProductOrder__wrapper">
				<div>
					<Typography
						component="h1"
						variant="h3"
						sx={{ fontWeight: 600, mb: '30px', textAlign: 'center' }}
					>
						{t('order')} {productName}
					</Typography>
					<PropductOrderStepper currentStep={activeStep} />
					{productData && (
						<div className="ProductOrder__column-wrapper">
							{activeStep === 0 && (
								<div
									className={`ProductOrder__form ${isFormStep ? 'active' : ''}`}
									onClick={() => {
										setIsFormStep(true);
									}}
								>
									<ProductOrderForm
										saveFormValues={saveFormValues}
										product={productData}
									/>
								</div>
							)}
							{(activeStep === 1 || activeStep === 2) && currentOrderNumber && (
								<div
									className={`ProductOrder__form ${isFormStep ? 'active' : ''}`}
									onClick={() => {
										setIsFormStep(true);
									}}
								>
									<CalendarSlotSelection
										currentOrderNumber={currentOrderNumber}
										onTimeSelection={onCalendlyTimeSelection}
										onScheduled={onCalendlyScheduled}
									/>
								</div>
							)}

							{activeStep === 3 && currentOrderNumber && (
								<ServicePaymentPage orderNumber={currentOrderNumber} />
							)}

							{activeStep !== 3 && (
								<div className={`ProductOrder__summary ${!isFormStep ? 'active' : ''}`}>
									{!!(productData && productName) && (
										<>
											<ProductOrderSummary
												orderFormValues={orderFormValues}
												product={productData}
												productName={productName}
												fields={currentFormFields}
												onPurchase={onPurchase}
												isActive={!isFormStep}
											/>
											{/* <CalendarSlotSelection /> */}
										</>
									)}
								</div>
							)}
						</div>
					)}
				</div>
				{isLoading && <PageLoader />}
			</div>
		</div>
	);
};

export default ProductOrder;
